import { InspectionItemStatus } from '@/shared/enums/InspectionStatus';

export const AUTOMATIC_FLOW_STATUSES = [
  InspectionItemStatus.ProcessandoScraping,
  InspectionItemStatus.AguardandoAnaliseIA,
  InspectionItemStatus.ProcessandoAnaliseIA,
  InspectionItemStatus.LaudoAutomaticoGerado,
  InspectionItemStatus.ScrapingFalhou,
  InspectionItemStatus.AnaliseIAFalhou,
] as const;

export const PROCESSING_STATUSES = [
  InspectionItemStatus.ProcessandoScraping,
  InspectionItemStatus.AguardandoAnaliseIA,
  InspectionItemStatus.ProcessandoAnaliseIA,
] as const;

export const FAILURE_STATUSES = [
  InspectionItemStatus.ScrapingFalhou,
  InspectionItemStatus.AnaliseIAFalhou,
] as const;

export const REJECTION_STATUSES = [
  InspectionItemStatus.LaudoNegadoPorPrestadora,
  InspectionItemStatus.LaudoNegadoPorBanco,
  InspectionItemStatus.CorrecaoPeloInspetorBanco,
] as const;

export function isAutomaticStatusFlow(status: InspectionItemStatus | undefined): boolean {
  if (status === undefined) return false;
  return AUTOMATIC_FLOW_STATUSES.includes(status as typeof AUTOMATIC_FLOW_STATUSES[number]);
}

export function isProcessingStatus(status: InspectionItemStatus | undefined): boolean {
  if (status === undefined) return false;
  return PROCESSING_STATUSES.includes(status as typeof PROCESSING_STATUSES[number]);
}

export function isFailureStatus(status: InspectionItemStatus | undefined): boolean {
  if (status === undefined) return false;
  return FAILURE_STATUSES.includes(status as typeof FAILURE_STATUSES[number]);
}

export function isRejectionStatus(status: InspectionItemStatus | undefined): boolean {
  if (status === undefined) return false;
  return REJECTION_STATUSES.includes(status as typeof REJECTION_STATUSES[number]);
}

export function getStageFromStatus(status: InspectionItemStatus | undefined): 'scraping' | 'analysis' | 'completed' | 'failed' | 'unknown' {
  switch (status) {
    case InspectionItemStatus.ProcessandoScraping:
      return 'scraping';
    case InspectionItemStatus.AguardandoAnaliseIA:
    case InspectionItemStatus.ProcessandoAnaliseIA:
      return 'analysis';
    case InspectionItemStatus.LaudoAutomaticoGerado:
      return 'completed';
    case InspectionItemStatus.ScrapingFalhou:
    case InspectionItemStatus.AnaliseIAFalhou:
      return 'failed';
    default:
      return 'unknown';
  }
}

export function normalizeStatus(rawStatus: string | number | undefined): InspectionItemStatus | undefined {
  if (rawStatus === undefined || rawStatus === null) return undefined;
  return typeof rawStatus === 'string' ? parseInt(rawStatus, 10) : rawStatus;
}

interface StatusMessageConfig {
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  description: string;
}

interface GetStatusMessageParams {
  itemStatus: InspectionItemStatus | undefined;
  reportsCount: number;
  isInspector: boolean;
  isProvider: boolean;
  isBank: boolean;
}

export function getStatusMessage({
  itemStatus,
  reportsCount,
  isInspector,
  isProvider,
  isBank,
}: GetStatusMessageParams): StatusMessageConfig | null {
  const isAutomaticReportFlow = itemStatus === InspectionItemStatus.AguardandoPreenchimentoPrestadora;
  const isAwaitingProviderReview = itemStatus === InspectionItemStatus.AguardandoRevisaoPrestadora;
  const isAwaitingBankReview = itemStatus === InspectionItemStatus.AguardandoRevisaoBanco;
  const isRejectedByBank = itemStatus === InspectionItemStatus.LaudoNegadoPorBanco;
  const isApprovedByBank = itemStatus === InspectionItemStatus.LaudoAceitoPeloBanco;

  if (isAutomaticReportFlow && reportsCount === 0) {
    if (isProvider) {
      return {
        type: 'warning',
        message: 'Laudo Automático - Ação Necessária',
        description: 'Este tipo de inspeção não requer inspetor. Preencha o laudo de avaliação e envie diretamente para o Banco.',
      };
    }
    if (isBank) {
      return {
        type: 'info',
        message: 'Aguardando Laudo da Prestadora',
        description: 'A Prestadora está preenchendo o laudo de avaliação (sem inspetor).',
      };
    }
  }

  if (isAwaitingProviderReview && reportsCount === 0) {
    if (isInspector) {
      return {
        type: 'info',
        message: 'Aguardando Revisão da Prestadora',
        description: 'O checklist foi preenchido e enviado. Aguardando a Prestadora gerar o laudo.',
      };
    }
    if (isProvider) {
      return {
        type: 'warning',
        message: 'Ação Necessária: Gerar Laudo',
        description: 'O Inspetor completou o checklist. Gere o laudo de avaliação.',
      };
    }
  }

  if (isAwaitingBankReview) {
    if (isInspector || isProvider) {
      return {
        type: 'info',
        message: 'Aguardando Revisão do Banco',
        description: 'O laudo foi gerado pela Prestadora e está aguardando a revisão do Banco.',
      };
    }
    if (isBank) {
      return {
        type: 'warning',
        message: 'Ação Necessária: Revisar Laudo',
        description:
          'O laudo foi gerado pela Prestadora e está aguardando sua revisão. Aprove ou aponte correções necessárias.',
      };
    }
  }

  if (isRejectedByBank) {
    if (isInspector) {
      return {
        type: 'info',
        message: 'Laudo Rejeitado pelo Banco',
        description: 'O Banco rejeitou o laudo. A Prestadora está fazendo as correções.',
      };
    }
    if (isProvider) {
      return {
        type: 'warning',
        message: 'Ação Necessária: Corrigir Laudo',
        description:
          'O Banco apontou correções no laudo. Faça as correções e reenvie.',
      };
    }
    if (isBank) {
      return {
        type: 'info',
        message: 'Laudo Rejeitado',
        description: 'Você rejeitou o laudo. Aguardando correções da Prestadora.',
      };
    }
  }

  if (isApprovedByBank) {
    return {
      type: 'success',
      message: 'Laudo Aprovado',
      description: 'O laudo foi aprovado pelo banco. Processo concluído com sucesso!',
    };
  }

  return null;
}
